import React, { useState, useMemo } from 'react';
import { Search, ChevronDown, Filter } from 'lucide-react';
import faqData from "../data/faqData";
import useMetaTags from "../hooks/useMetaTags"; // Adjust path based on your project structure

const FAQPage = () => {
  useMetaTags({
    title: "FAQs | Organic Pads - Get Answers About Eco-Friendly Menstrual Care",
    description: "Find answers to common questions about organic sanitary pads, their benefits, usage, and sustainability. Learn how our eco-friendly period products work.",
    keywords: "organic pads FAQ, eco-friendly menstrual care, sustainable period products, organic sanitary pads questions, chemical-free pads benefits",
    author: "Organic Pads",
    robots: "index, follow",
    canonical: "/faqs",
    ogTitle: "FAQs | Organic Pads - Get Answers About Eco-Friendly Menstrual Care",
    ogDescription: "Explore frequently asked questions about organic sanitary pads, their advantages, and how they contribute to a healthier planet.",
    ogImage: "https://organicpads.shop/logo.png",
    ogUrl: "https://organicpads.shop/faqs",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterTitle: "FAQs | Organic Pads - Get Answers About Eco-Friendly Menstrual Care",
    twitterDescription: "Curious about organic sanitary pads? Check out our FAQs to learn more about their benefits and sustainability.",
    twitterImage: "https://organicpads.shop/logo.png",
    twitterSite: "@OrganicPads",
    twitterCreator: "@OrganicPads",
  });
  
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": Object.values(faqData).flatMap(category => 
      category.questions.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    )
  };
  
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [openQuestions, setOpenQuestions] = useState(new Set());
  const [selectedTags, setSelectedTags] = useState(new Set());

  // Get all unique tags from questions
  const allTags = useMemo(() => {
    const tags = new Set();
    Object.values(faqData).forEach(category => {
      category.questions.forEach(q => {
        q.tags.forEach(tag => tags.add(tag));
      });
    });
    return Array.from(tags);
  }, []);

  // Filter questions based on search, category, and tags
  const filteredQuestions = useMemo(() => {
    let questions = [];
    
    Object.entries(faqData).forEach(([categoryKey, category]) => {
      if (selectedCategory === "all" || selectedCategory === categoryKey) {
        questions.push(...category.questions);
      }
    });

    // Filter by search query
    if (searchQuery) {
      questions = questions.filter(q => 
        q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.answer.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by selected tags
    if (selectedTags.size > 0) {
      questions = questions.filter(q =>
        q.tags.some(tag => selectedTags.has(tag))
      );
    }

    return questions;
  }, [searchQuery, selectedCategory, selectedTags]);

  const toggleQuestion = (id) => {
    setOpenQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleTag = (tag) => {
    setSelectedTags(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tag)) {
        newSet.delete(tag);
      } else {
        newSet.add(tag);
      }
      return newSet;
    });
  };

  return (
    <>
    <div className="min-h-screen bg-gray-50 pb-10 pt-20">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <h1 className="text-4xl font-bold text-center text-[#679729] mb-2">
          Frequently Asked Questions
        </h1>
        <p className="text-center text-gray-600 mb-8">
          Find answers to all your questions about organic pads
        </p>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-8">
          {/* Search Bar */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search questions..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#679729]/20"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Category Selector */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => setSelectedCategory("all")}
              className={`px-4 py-2 rounded-full text-sm ${
                selectedCategory === "all"
                  ? "bg-[#679729] text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              All
            </button>
            {Object.entries(faqData).map(([key, category]) => (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={`px-4 py-2 rounded-full text-sm ${
                  selectedCategory === key
                    ? "bg-[#679729] text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2">
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                className={`px-3 py-1 rounded-full text-xs ${
                  selectedTags.has(tag)
                    ? "bg-[#679729]/20 text-[#679729]"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                #{tag}
              </button>
            ))}
          </div>
        </div>

        {/* Questions List */}
        <div className="space-y-4">
          {filteredQuestions.map((faq) => (
            <div
              key={faq.id}
              className="border border-[#679729]/20 rounded-lg overflow-hidden bg-white shadow-sm"
            >
              <button
                className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-[#679729]/10 transition-colors"
                onClick={() => toggleQuestion(faq.id)}
              >
                <span className="font-medium text-[#679729]">{faq.question}</span>
                <ChevronDown
                  className={`transform transition-transform ${
                    openQuestions.has(faq.id) ? 'rotate-180' : ''
                  } text-[#679729]`}
                  size={20}
                />
              </button>
              <div
                className={`px-6 transition-all duration-200 ease-in-out ${
                  openQuestions.has(faq.id) ? 'py-4 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                }`}
              >
                <p className="text-gray-600 mb-2">{faq.answer}</p>
                <div className="flex gap-2">
                  {faq.tags.map(tag => (
                    <span key={tag} className="text-xs text-[#679729]/70">#{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Results Message */}
        {filteredQuestions.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No questions found matching your criteria
          </div>
        )}
      </div>
    </div>
    {/* Schema Markup */}
    <script type="application/ld+json">
        {JSON.stringify(schemaMarkup)}
      </script>
    </>
  );
};

export default FAQPage;